import { DatePipe, Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import * as mime from 'mime';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/internal/operators/catchError';
import { finalize, first, map, mergeMap, tap } from 'rxjs/operators';
import { Action } from 'src/app/jobs-shared/job-data/models/action';
import { LicenseDataService } from 'src/app/new-license-scan/license-data.service';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { LicenseUploadBottomSheetComponent, LicenseUploadResult } from 'src/app/shared/components/license-upload-bottom-sheet/license-upload-bottom-sheet.component';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { ConfirmationDialogComponent } from 'src/app/shared/dialog/confitmation-dialog/confirmation-dialog.component';
import { ImageLightboxData, ImageLightboxDialogComponent } from 'src/app/shared/dialog/image-lightbox-dialog/image-lightbox-dialog.component';
import { ResidenceHistoryDialogComponent } from 'src/app/shared/dialog/residence-history-dialog/residence-history-dialog.component';
import { FileDataService } from 'src/app/shared/file-data/file-data.service';
import { License } from 'src/app/shared/models/license.model';
import { LICENSE_CLASSES_ALL } from 'src/app/shared/models/document-extract/document-extract-options';
import { CANADA_PROVINCES, USA_PROVINCES } from 'src/app/shared/models/provinces';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';
import { DriverQualificationService } from 'src/app/driver-qualification-files/service/driver-qualification.service';
import { getDocumentFileExtension } from 'src/app/shared/utils/document-file-extension.util';

const ACTION_UPDATE_LICENSE = 'Update License';
const BERBIX_STATUS_ACTIVE = 'active';
const BERBIX_STATUS_FAILED = 'failed';
const BERBIX_STATUS_PROCESSING = 'processing';
const PROCESSSING_STATUS_MESSAGE = `<span>Your license information is being processed, it will take</span
><strong> few minutes </strong><span>to complete the process</span>`;
const FAILED_STATUS_MESSAGE = `<span>We were unable to process the scanned driver license. Please fill your driver license information below</span>`;
export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY'
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};
@Component({
  selector: 'app-profile-license-information',
  templateUrl: './profile-license-information.component.html',
  styleUrls: ['./profile-license-information.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    DatePipe
  ],
  standalone: false
})
export class ProfileLicenseInformationComponent implements OnInit, OnDestroy {
  licenseForm = new UntypedFormGroup({});
  REQUIRED_FIELD_MESSAGE = 'You must enter a valid value';
  loading: boolean = false;
  issuingCountry = [
    { code: 'ca', value: 'Canada' },
    { code: 'us', value: 'USA' }
  ];
  issuingProvinces = CANADA_PROVINCES;
  issuingStateProvinces = [];
  usStates = USA_PROVINCES;
  stateProvinces = [];
  provinceLicenseClass = LICENSE_CLASSES_ALL;
  licenseClass = [];
  isExplain = false;
  errorMessage = null;
  driverLicenseId = null;
  currentStatus: string;
  currentStatusMessage: string;
  maxDate = new Date();
  expiryMinDate: Date = new Date();
  private subs = new SubSink();
  licenseLoaded: boolean;
  licenseExists: boolean;
  frontImageExists: boolean = false;
  backImageExists: boolean = false;
  isAirBrakeRequired: boolean = false;
  private initialFormValue: any;
  private userHasModifiedForm: boolean = false;
  private isInitializing: boolean = true;
  private extractedLicenseData: any = null; // Store extracted data for later use

  // New properties for inline image display
  frontImageUrl: SafeUrl | null = null;
  backImageUrl: SafeUrl | null = null;
  frontImageFile: File | null = null;
  backImageFile: File | null = null;
  isUploadingImage: boolean = false;
  showFormManually: boolean = false; // Flag to show form manually for first-time users

  // Saving progress tracking
  isSaving: boolean = false;
  savingStep: 'creating' | 'updating' | 'uploading' | 'refreshing' | null = null;

  /**
   * Check if images are required for save
   * For new licenses: both images must be uploaded
   * For existing licenses: images are optional (may already exist)
   */
  get areImagesRequired(): boolean {
    return !this.driverLicenseId;
  }

  /**
   * Check if both required images are uploaded
   */
  get areBothImagesUploaded(): boolean {
    return this.frontImageExists && this.backImageExists;
  }

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly location: Location,
    private readonly registrationService: RegistrationService,
    private readonly driverApiService: DriverApiService,
    private readonly timeService: DateTimeService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastService: ToastService,
    private readonly dialog: MatDialog,
    private readonly bottomSheet: MatBottomSheet,
    private readonly domSanitizer: DomSanitizer,
    private readonly licenseDataService: LicenseDataService,
    private readonly fileDataService: FileDataService,
    private readonly driverQualificationService: DriverQualificationService
  ) {
    this.loading = false;
    this.createForm();
  }

  ngOnInit(): void {
    // Check for extracted license data from Document Extract API
    this.extractedLicenseData = this.registrationService.getExtractedLicenseData();

    this.getLicense(true, false);

    // Track user modifications - but ignore changes during initialization
    this.licenseForm.valueChanges.subscribe(() => {
      if (!this.isInitializing && this.initialFormValue) {
        this.userHasModifiedForm = true;
      }
    });

    this.licenseForm.get('issuingProvince').valueChanges.subscribe(element => {
      if (element === 'ON') {
        this.licenseForm.get('hasAirBreakEndorsment').setValidators([Validators.required]);
        this.isAirBrakeRequired = true;
      } else {
        this.licenseForm.get('hasAirBreakEndorsment').setValidators(null);
        this.isAirBrakeRequired = false;
      }
      this.licenseForm.get('hasAirBreakEndorsment').updateValueAndValidity();
    });

    this.licenseForm.get('hasDeniedLicense').valueChanges.subscribe(element => {
      this.updateControlValidity(element, 'deniedLicenseExplanation');
    });

    this.licenseForm.get('hasSuspendedLicense').valueChanges.subscribe(element => {
      this.updateControlValidity(element, 'suspendedLicenseExplanation');
    });
    Object.keys(this.licenseForm.controls).forEach((key) => {
      const control = this.licenseForm.get(key);
      if (control?.invalid) {
        control.markAsTouched();
        control.markAsDirty();
      }
    });

  }

  createForm(): void {
    this.licenseForm = this.fb.group({
      licenseNumber: ['', Validators.required],
      issueDate: ['', Validators.required],
      expiryDate: ['', Validators.required],
      issuingCountry: ['', Validators.required],
      issuingProvince: ['', Validators.required],
      licenseType: ['', Validators.required],
      hasAirBreakEndorsment: [null], // Only required for Ontario (ON) province
      hasDeniedLicense: [null, Validators.required],
      deniedLicenseExplanation: [null], // Only required when hasDeniedLicense is true
      hasSuspendedLicense: [null, Validators.required],
      suspendedLicenseExplanation: [null] // Only required when hasSuspendedLicense is true
    });
  }

  issueDateChangeEvent(event: MatDatepickerInputEvent<Date>) {
    this.setMinDateFormExpiry(event.value);
  }

  onCountryChange(countryCode: string): void {
    this.setStateProvinces(countryCode);
    this.licenseForm.get('licenseType').patchValue(null);
  }

  private setStateProvinces(countryCode): void {
    this.stateProvinces = countryCode === 'ca' ? this.issuingProvinces : this.usStates;
  }

  onProvinceChange(province: string): void {
    this.licenseForm.get('licenseType').patchValue(null);
    this.setLicense(province);
  }

  setDeniedLicense(value): void {
    this.licenseForm.get('hasDeniedLicense').patchValue(value);
  }

  setAirBrakeEndorsement(value: boolean): void {
    this.licenseForm.get('hasAirBreakEndorsment').patchValue(value);
  }

  setPermitSuspended(value: boolean): void {
    this.licenseForm.get('hasSuspendedLicense').patchValue(value);
  }

  goBack(): void {
    // Check if there are unsaved changes
    if (this.hasUnsavedChanges()) {
      this.showWarningMessage();
    } else {
      this.location.back();
    }
  }

  navigateToProfile(): void {
    this.registrationService.navigateToProfile();
  }

  /**
   * Refresh driver data and navigate to profile
   * This ensures the profile dashboard shows the latest license completion status
   * TEMPORARY WORKAROUND: Also fetch full license details to check declaration fields
   */
  private refreshAndNavigateToProfile(): void {

    this.driverApiService.getDriver().pipe(
      first(),
      tap((response) => {
      }),
      // WORKAROUND: Fetch full license details to check if requirements are actually met
      mergeMap((driverResponse) => {
        if (this.driverLicenseId) {
          return this.registrationService.getLicense(this.driverLicenseId).pipe(
            first(),
            map((licenseResponse) => ({ driverResponse, licenseResponse })),
            catchError((error) => {
              console.error('[LICENSE SAVE] ERROR fetching license details:', error);
              return of({ driverResponse, licenseResponse: null });
            })
          );
        }
        return of({ driverResponse, licenseResponse: null });
      }),
      catchError((error) => {
        console.error('[LICENSE SAVE] ERROR in refresh flow:', error);
        // Navigate anyway even if refresh fails
        return of(null);
      })
    ).subscribe(() => {
      // Return to DQF context when available
      const dqfId = this.driverQualificationService?.dqfId;
      if (dqfId) {
        this.router.navigate(['driverQualification/documents', dqfId]);
        return;
      }

      // URL shows DQF flow even if dqfId missing
      if (this.router.url.includes('driverQualification') || this.router.url.includes('/dqf/')) {
        this.router.navigate(['driverQualification']);
        return;
      }

      this.navigateToProfile();
    });
  }

  private hasUnsavedChanges(): boolean {
    // If form hasn't been initialized yet, no changes to lose
    if (!this.initialFormValue) {
      return false;
    }

    // If user hasn't modified the form at all, no need to warn
    if (!this.userHasModifiedForm) {
      return false;
    }

    const currentValue = this.licenseForm.value;

    // Check if there are any changes from initial value
    const hasChanges = JSON.stringify(currentValue) !== JSON.stringify(this.initialFormValue);

    if (!hasChanges) {
      return false;
    }

    // If there are changes, check if any mandatory fields have been filled
    const hasMandatoryFieldsFilled =
      currentValue.licenseNumber ||
      currentValue.issueDate ||
      currentValue.expiryDate ||
      currentValue.issuingCountry ||
      currentValue.issuingProvince ||
      currentValue.licenseType ||
      currentValue.hasDeniedLicense !== null ||
      currentValue.hasSuspendedLicense !== null;

    // Only show warning if user modified form, there are changes, AND mandatory fields are filled
    return hasMandatoryFieldsFilled;
  }

  private showWarningMessage(): void {
    const actions = this.createDialogActions();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Unsaved Changes',
        message: 'If you go back now, any license information you\'ve entered will be lost. Do you want to continue?',
        actions
      },
      disableClose: true
    });

    const proceedAction = actions[1];
    const cancelAction = actions[0];
    proceedAction.action = () => {
      this.confirmNavigateBack(dialogRef, proceedAction, cancelAction);
      return {} as any;
    };
    cancelAction.action = () => {
      this.cancelNavigation(dialogRef, proceedAction, cancelAction);
      return {} as any;
    };

    dialogRef.afterClosed().subscribe();
  }

  private confirmNavigateBack(dialogRef: MatDialogRef<ConfirmationDialogComponent>, proceedAction: Action, cancelAction: Action): void {
    proceedAction.disabled = true;
    cancelAction.disabled = true;
    dialogRef.close();
    this.navigateToProfile();
  }

  private cancelNavigation(dialogRef: MatDialogRef<ConfirmationDialogComponent>, proceedAction: Action, cancelAction: Action): void {
    proceedAction.disabled = true;
    cancelAction.disabled = true;
    dialogRef.close();
  }

  private createDialogActions(): Action[] {
    const actions: Action[] = [];

    const cancelAction = {
      name: 'Cancel',
      action: (data: any) => { },
      disabled: false
    } as Action;
    actions.push(cancelAction);

    const proceedAction = {
      name: 'Proceed',
      action: (data: any) => { },
      disabled: false,
      color: 'primary'
    } as Action;
    actions.push(proceedAction);

    return actions;
  }

  onSubmit(): void {
    // Mark all fields as touched to show validation errors
    Object.keys(this.licenseForm.controls).forEach((key) => {
      const control = this.licenseForm.get(key);
      control.markAsTouched();
      control.markAsDirty();
    });

    if (this.licenseForm.valid) {
      const model = new License(this.licenseForm.value);

      if (!model.issueDate) {
        delete model.issueDate;
      }
      model.issueDate = new Date(new Date(model.issueDate).setHours(0, 0, 0, 0)).toISOString().replace('.000Z', 'Z');
      model.expiryDate = new Date(new Date(model.expiryDate).setHours(0, 0, 0, 0)).toISOString().replace('.000Z', 'Z');

      if (this.driverLicenseId) {
        // Update existing license
        this.updateLicense(model);
      } else {
        // Create new license for fresh drivers
        this.createLicense(model);
      }
    }
  }

  updateControlValidity(element: any, formControlName: string) {
    if (element) {
      this.licenseForm.get(formControlName).setValidators([Validators.required]);
    } else {
      this.licenseForm.get(formControlName).setValidators(null);
      this.licenseForm.get(formControlName).patchValue(null);
    }
    this.licenseForm.get(formControlName).updateValueAndValidity();
  }

  /**
   * Create new license for fresh drivers
   * CORRECTED 2-STEP FLOW:
   * Step 1: POST with extensions only → Sets file locations
   * Step 2: PUT with form data + extensions → Validates all 12 fields
   * Step 3: Upload images using signed URLs from Step 2
   */
  private createLicense(model: License): void {
    this.loading = true;
    this.isSaving = true;
    this.savingStep = 'creating';

    // Store the flag BEFORE we change it
    const wasNewLicense = !this.licenseExists;

    // STEP 1: Send ONLY extensions to create license and set file locations
    const createModel: any = {};

    if (this.frontImageFile) {
      const frontExt = this.frontImageFile.name.split('.').pop()?.toLowerCase() || 'png';
      createModel.frontScanImageExtension = frontExt;
    }
    if (this.backImageFile) {
      const backExt = this.backImageFile.name.split('.').pop()?.toLowerCase() || 'png';
      createModel.backScanImageExtension = backExt;
    }

    this.subs.sink = this.driverApiService
      .uploadLicense(createModel)
      .pipe(
        catchError((error: any) => {
          this.errorMessage = environment.errorMessage;
          this.loading = false;
          this.isSaving = false;
          this.savingStep = null;
          return of(false);
        })
      )
      .subscribe((response) => {
        if (response && response.data) {
          this.errorMessage = undefined;
          this.driverLicenseId = response.data.id;
          this.licenseExists = true;

          // Store response for Step 3 (signed URLs)
          this.registrationService.updateLicenseStore(response.data);

          // STEP 2: Update with form data + extensions
          this.updateLicenseAfterCreate(model, wasNewLicense);
        } else {
          this.loading = false;
          this.isSaving = false;
          this.savingStep = null;
        }
      });
  }

  /**
   * Update license with form data after initial creation
   * CORRECTED: Include extensions in Step 2 to get fresh signed URLs and trigger validation
   * Backend generates new file paths (overwrites Step 1 paths) but returns fresh signed URLs
   */
  private updateLicenseAfterCreate(model: License, wasNewLicense: boolean = false): void {
    // Build update model with ALL fields including extensions
    const updateModel: any = {
      // 6 Core license fields
      licenseNumber: model.licenseNumber,
      issueDate: model.issueDate,
      expiryDate: model.expiryDate,
      issuingCountry: model.issuingCountry,
      issuingProvince: model.issuingProvince,
      licenseType: model.licenseType,

      // 3 Declaration booleans
      hasAirBreakEndorsment: model.hasAirBreakEndorsment ?? false,
      hasDeniedLicense: model.hasDeniedLicense ?? false,
      hasSuspendedLicense: model.hasSuspendedLicense ?? false,

      // 2 Explanation fields (even if empty)
      deniedLicenseExplanation: model.deniedLicenseExplanation || '',
      suspendedLicenseExplanation: model.suspendedLicenseExplanation || ''
    };

    // ✅ INCLUDE extensions to get fresh signed URLs and trigger validation
    if (this.frontImageFile) {
      const frontExt = this.frontImageFile.name.split('.').pop()?.toLowerCase() || 'png';
      updateModel.frontScanImageExtension = frontExt;
    }
    if (this.backImageFile) {
      const backExt = this.backImageFile.name.split('.').pop()?.toLowerCase() || 'png';
      updateModel.backScanImageExtension = backExt;
    }

    this.savingStep = 'updating';

    this.subs.sink = this.registrationService
      .updateLicense(updateModel, this.driverLicenseId)
      .pipe(
        catchError((error: any) => {
          this.errorMessage = environment.errorMessage;
          this.loading = false;
          this.isSaving = false;
          this.savingStep = null;
          return of(false);
        })
      )
      .subscribe((response) => {
        if (response) {
          this.errorMessage = undefined;

          this.savingStep = 'uploading';

          // Upload images using fresh signed URLs from Step 2 response
          // For fresh drivers, wait for image upload to complete before navigating
          this.uploadImagesAfterSave(response, true, () => {

            this.savingStep = 'refreshing';

            // WORKAROUND: Trigger backend validation after images are uploaded
            // The backend has a bug where it doesn't include file locations in validation check
            // Making a minimal update call triggers re-validation with uploaded images present
            const currentLicense = this.registrationService.getLicenseStore();
            this.registrationService
              .updateLicense({ licenseType: currentLicense?.licenseType || model.licenseType }, this.driverLicenseId)
              .pipe(
                first(),
                catchError((error) => {
                  // Continue even if this fails - user can save again if needed
                  return of(null);
                })
              )
              .subscribe(() => {

                // Now refresh license data to get updated licenseRequirementsCompleted status
                this.registrationService.getLicense(this.driverLicenseId).pipe(
                  first(),
                  catchError((error) => {
                    return of(null);
                  })
                ).subscribe((refreshedLicense) => {
                  if (refreshedLicense) {
                    this.registrationService.updateLicenseStore(refreshedLicense);
                  }

                  this.toastService.showSuccess('License information saved successfully!');
                  this.initialFormValue = this.licenseForm.value;
                  this.userHasModifiedForm = false;
                  this.loading = false;
                  this.isSaving = false;
                  this.savingStep = null;

                  // Use common post-save logic
                  this.proceedAfterLicenseSave(model, wasNewLicense);
                });
              });
          });
        } else {
          this.loading = false;
          this.isSaving = false;
          this.savingStep = null;
        }
      });
  }

  /**
   * Check driver profile and auto-update empty fields from extracted data
   */
  private checkAndAutoUpdateDriverProfile(extractedData: any, isNewLicense: boolean = false): void {
    this.loading = true;

    // Get driver profile to check what needs updating
    this.driverApiService.getDriver().pipe(
      first(),
      map((response: any) => response.data), // Extract data from response
      catchError((error) => {
        // If we can't get driver profile, just show success and navigate
        this.loading = false;
        this.navigateToProfile();
        return of(null);
      })
    ).subscribe((driver) => {
      if (!driver) {
        this.loading = false;
        this.navigateToProfile();
        return;
      }

      // Check what needs updating
      const needsDobUpdate = !driver.birthday && extractedData.date_of_birth;
      const needsGenderUpdate = !driver.gender && extractedData.gender;
      const needsAddressUpdate = !driver.address && this.hasAllAddressFields(extractedData);


      if (!needsDobUpdate && !needsGenderUpdate && !needsAddressUpdate) {
        // Nothing to auto-update, but we still might need to show residence history dialog
        this.loading = false;

        // If there's a full address, show residence history confirmation dialog
        if (extractedData.full_address) {
          this.showResidenceHistoryConfirmationDialog(extractedData.full_address, new Date());
        } else {
          this.refreshAndNavigateToProfile();
        }
        return;
      }

      // Perform auto-update
      this.performDriverProfileAutoUpdate(driver.id, extractedData, needsDobUpdate, needsGenderUpdate, needsAddressUpdate, isNewLicense);
    });
  }

  /**
   * Check if all address fields are present in extracted data
   */
  private hasAllAddressFields(extractedData: any): boolean {
    return !!(
      extractedData.address_street &&
      extractedData.address_city &&
      extractedData.address_subdivision &&
      extractedData.address_postal_code &&
      extractedData.address_country
    );
  }

  /**
   * Perform driver profile auto-update with extracted data
   */
  private performDriverProfileAutoUpdate(
    driverId: string,
    extractedData: any,
    updateDob: boolean,
    updateGender: boolean,
    updateAddress: boolean,
    isNewLicense: boolean = false
  ): void {
    // Build update payload
    const updatePayload: any = {};

    if (updateDob) {
      updatePayload.birthday = extractedData.date_of_birth; // YYYY-MM-DD format
    }

    if (updateGender) {
      // Only update gender if DOB is also being updated or gender field is empty
      updatePayload.gender = extractedData.gender; // "male" or "female"
    }

    if (updateAddress) {
      const completeAddress = this.getCompleteAddressFromExtractedData(extractedData);
      
      updatePayload.address = completeAddress;
      updatePayload.city = extractedData.address_city;
      updatePayload.state = this.normalizeProvinceForDriver(extractedData.address_subdivision, extractedData.address_country);
      updatePayload.zip = extractedData.address_postal_code;
      updatePayload.country = this.getFullCountryName(extractedData.address_country);
    }

    // Open dialog IMMEDIATELY with loading state
    const dialogData: any = {
      isUpdatingProfile: true, // Show loading state
      dobUpdated: updateDob,
      addressUpdated: updateAddress,
      dateOfBirth: extractedData.date_of_birth,
      address: extractedData.full_address,
      hasResidenceHistory: !!extractedData.full_address
    };

    const dialogRef = this.dialog.open(ResidenceHistoryDialogComponent, {
      width: '500px',
      maxWidth: '95vw',
      data: dialogData,
      disableClose: true,
      hasBackdrop: true
    });

    // Update driver profile WHILE dialog is open
    this.driverApiService.updateDriver(updatePayload).pipe(
      first(),
      catchError((error) => {
        console.error('[LICENSE SAVE] ERROR updating driver profile:', error);
        // Close dialog and navigate on error
        dialogRef.close();
        this.loading = false;
        this.refreshAndNavigateToProfile();
        return of(null);
      })
    ).subscribe((response) => {
      this.loading = false;

      if (response) {
        // Get license issue date
        this.registrationService.getLicense(this.driverLicenseId).pipe(
          first(),
          catchError(() => of(null))
        ).subscribe((license) => {
          const issueDate = license?.issueDate || new Date();

          if (isNewLicense && extractedData.full_address) {
            this.createResidenceHistoryEntry(extractedData.full_address, issueDate).subscribe((historyCreated) => {
              // Update dialog to show comprehensive success message
              dialogData.isUpdatingProfile = false;
              dialogData.showSuccessMessage = true;
              dialogData.residenceHistoryUpdated = historyCreated;

              dialogRef.componentInstance.data = { ...dialogData };

              // Auto-close after 2.5 seconds and navigate
              setTimeout(() => {
                dialogRef.close(true);
                this.registrationService.clearExtractedLicenseData();
                this.refreshAndNavigateToProfile();
              }, 2500);
            });
          } else {
            // FOR EXISTING LICENSES: Show residence history question
            dialogData.isUpdatingProfile = false;
            dialogData.showResidenceHistory = true;
            dialogData.licenseIssueDate = issueDate;

            dialogRef.componentInstance.data = { ...dialogData };

            // User must respond to dialog
            dialogRef.afterClosed().subscribe((result) => {
              if (result && extractedData.full_address) {
                // User confirmed - update residence history
                this.showResidenceHistoryConfirmationDialog(extractedData.full_address, issueDate);
              } else {
                // User declined or cancelled
                this.registrationService.clearExtractedLicenseData();
                this.refreshAndNavigateToProfile();
              }
            });
          }
        });
      } else {
        // API returned but no response
        dialogRef.close();
        this.refreshAndNavigateToProfile();
      }
    });
  }

  /**
   * Normalize province code for driver profile
   */
  private normalizeProvinceForDriver(province: string, country: string): string {
    if (!province) return '';

    const normalizedCountry = this.normalizeCountryCode(country);
    const provinceUpper = province.toUpperCase();

    // For US states, add US- prefix
    if (normalizedCountry === 'US') {
      return `US-${provinceUpper}`;
    }

    // For Canadian provinces, return as-is
    return provinceUpper;
  }

  /**
   * Silently create residence history entry for new licenses
   */
  private createResidenceHistoryEntry(address: string, fromDate: Date | string): Observable<boolean> {
    // Convert fromDate to Date if it's a string
    const dateObj = typeof fromDate === 'string' ? new Date(fromDate) : fromDate;

    // Format the fromDate to first day of the month
    const formattedFromDate = dayjs(dateObj).startOf('month').format('YYYY-MM-DD');

    // Get extracted data to access individual address fields from LLM
    const extractedData = this.registrationService.getExtractedLicenseData();

    let payload: any;

    // Check if we have individual address fields from extraction (preferred)
    if (extractedData && extractedData.address_street && extractedData.address_city &&
      extractedData.address_subdivision && extractedData.address_postal_code) {

      // Normalize country code to lowercase (API expects 'ca' or 'us', not 'CA' or 'US')
      let countryCode = (extractedData.address_country || 'CA').toUpperCase();
      if (countryCode === 'CANADA' || countryCode === 'CA') {
        countryCode = 'ca';
      } else if (countryCode === 'USA' || countryCode === 'US' || countryCode === 'UNITED STATES') {
        countryCode = 'us';
      } else {
        countryCode = countryCode.toLowerCase();
      }

      // Use individual address fields from extraction
      payload = {
        completeAddress: address,
        streetAddress: extractedData.address_street,
        city: extractedData.address_city,
        province: extractedData.address_subdivision,
        country: countryCode, // API expects lowercase: 'ca' or 'us'
        postalCode: extractedData.address_postal_code,
        livedFromDate: formattedFromDate,
        isCurrentAddress: true
        // NOTE: Do NOT include livedToDate for current addresses (isCurrentAddress: true)
      };
    } else {

      // Fall back to parsing the full address string
      const addressModel = this.parseResidenceAddress(address);

      // Normalize country code
      let countryCode = (addressModel.country || 'CA').toLowerCase();
      if (countryCode === 'canada') {
        countryCode = 'ca';
      } else if (countryCode === 'usa' || countryCode === 'united states') {
        countryCode = 'us';
      }

      payload = {
        completeAddress: address,
        streetAddress: addressModel.streetAddress,
        city: addressModel.city,
        province: addressModel.state,
        country: countryCode, // API expects lowercase: 'ca' or 'us'
        postalCode: addressModel.zipcode,
        livedFromDate: formattedFromDate,
        isCurrentAddress: true
        // NOTE: Do NOT include livedToDate for current addresses (isCurrentAddress: true)
      };
    }

    return this.registrationService.addAddress(payload).pipe(
      first(),
      map(() => {
        return true;
      }),
      catchError((error) => {
        console.error('[LICENSE SAVE] ERROR creating residence history:', error);
        // Return false but don't block the flow
        return of(false);
      })
    );
  }

  /**
   * Parse residence address from full address string
   * Expected format: "STREET CITY, PROVINCE, POSTALCODE, COUNTRY"
   * Example: "695 OLD TECUMSEH RD BELLE RIVER, ON, N0R 1A0, Canada"
   */
  private parseResidenceAddress(address: string): any {
    const addressModel = {
      streetAddress: '',
      city: '',
      state: '',
      zipcode: '',
      country: 'ca'
    };

    if (!address) {
      return addressModel;
    }

    // Split by comma
    const parts = address.split(',').map(p => p.trim());

    if (parts.length >= 4) {
      // Format: "STREET CITY, PROVINCE, POSTALCODE, COUNTRY"
      // Example: "695 OLD TECUMSEH RD BELLE RIVER, ON, N0R 1A0, Canada"

      // Part 0: Street address (may include city at the end)
      const streetPart = parts[0];
      // Try to extract city from end of street part
      const streetWords = streetPart.split(' ');
      if (streetWords.length > 3) {
        // Assume last 1-2 words are city
        addressModel.city = streetWords.slice(-2).join(' '); // Last 2 words as city
        addressModel.streetAddress = streetWords.slice(0, -2).join(' '); // Rest as street
      } else {
        addressModel.streetAddress = streetPart;
        addressModel.city = '';
      }

      // Part 1: Province/State
      addressModel.state = parts[1];

      // Part 2: Postal/Zip code
      addressModel.zipcode = parts[2];

      // Part 3: Country
      const countryStr = parts[3].toLowerCase();
      if (countryStr.includes('canada') || countryStr === 'ca') {
        addressModel.country = 'ca';
      } else if (countryStr.includes('usa') || countryStr.includes('united states') || countryStr === 'us') {
        addressModel.country = 'us';
      }
    } else if (parts.length === 3) {
      // Format: "STREET, CITY, PROVINCE/COUNTRY"
      addressModel.streetAddress = parts[0];
      addressModel.city = parts[1];
      addressModel.state = parts[2];
    }

    return addressModel;
  }

  /**
   * Normalize country code
   */
  private normalizeCountryCode(country: string): string {
    if (!country) return 'CA'; // Default to Canada

    const normalized = country.toUpperCase().trim();

    if (normalized === 'CANADA' || normalized === 'CA') {
      return 'CA';
    }
    if (normalized === 'USA' || normalized === 'US' || normalized === 'UNITED STATES') {
      return 'US';
    }

    return normalized;
  }

  /**
   * Get full country name for driver profile
   */
  private getFullCountryName(country: string): string {
    if (!country) return 'Canada'; // Default to Canada

    const normalized = country.toUpperCase().trim();

    if (normalized === 'CANADA' || normalized === 'CA') {
      return 'Canada';
    }
    if (normalized === 'USA' || normalized === 'US' || normalized === 'UNITED STATES') {
      return 'USA';
    }

    // Return as-is if not recognized
    return country;
  }

  /**
   * Get complete address with country from extracted data
   * Ensures the address format matches the validation pattern: "Street, City, ST ZIP, Country"
   */
  private getCompleteAddressFromExtractedData(extractedData: any): string {
    // Always reconstruct from components to ensure correct format
    // The LLM's full_address may have inconsistent comma placement
    if (!extractedData?.address_street || !extractedData?.address_city || 
        !extractedData?.address_subdivision || !extractedData?.address_postal_code) {
      return '';
    }

    const fullCountry = this.getFullCountryName(extractedData.address_country);
    
    // Construct address in the correct format: "Street, City, ST ZIP, Country"
    // Note: NO comma between state and postal code - they must be space-separated
    return `${extractedData.address_street}, ${extractedData.address_city}, ${extractedData.address_subdivision} ${extractedData.address_postal_code}, ${fullCountry}`;
  }

  

  private updateLicense(model: License): void {
    this.loading = true;
    this.isSaving = true;
    this.savingStep = 'updating';

    // Add image extensions to model if new images are uploaded
    if (this.frontImageFile) {
      model.frontScanImageExtension = getDocumentFileExtension(this.frontImageFile);
    }
    if (this.backImageFile) {
      model.backScanImageExtension = getDocumentFileExtension(this.backImageFile);
    }

    this.subs.sink = this.registrationService
      .updateLicense(model, this.driverLicenseId)
      .pipe(
        catchError((error: any) => {
          console.error('[LICENSE SAVE] ERROR in updateLicense:', error);
          this.errorMessage = environment.errorMessage;
          this.loading = false;
          this.isSaving = false;
          this.savingStep = null;
          return of(false);
        })
      )
      .subscribe((response) => {
        if (response) {
          this.errorMessage = undefined;

          // Check if new images need to be uploaded
          const hasNewImages = this.frontImageFile || this.backImageFile;

          if (hasNewImages) {
            this.savingStep = 'uploading';

            // Upload images and wait for completion before proceeding
            this.uploadImagesAfterSave(response, true, () => {
              this.savingStep = 'refreshing';

              // Refresh license data to get updated licenseRequirementsCompleted status
              this.registrationService.getLicense(this.driverLicenseId).pipe(
                first(),
                catchError((error) => {
                  return of(null);
                })
              ).subscribe((refreshedLicense) => {
                if (refreshedLicense) {
                  this.registrationService.updateLicenseStore(refreshedLicense);
                }

                this.toastService.showSuccess('License information saved successfully!');
                this.initialFormValue = this.licenseForm.value;
                this.userHasModifiedForm = false;
                this.loading = false;
                this.isSaving = false;
                this.savingStep = null;

                this.proceedAfterLicenseSave(model);
              });
            });
          } else {
            // No new images, just proceed
            this.toastService.showSuccess('License information saved successfully!');
            this.initialFormValue = this.licenseForm.value;
            this.userHasModifiedForm = false;
            this.loading = false;
            this.isSaving = false;
            this.savingStep = null;

            this.proceedAfterLicenseSave(model);
          }
        }
      });
  }

  /**
   * Common logic to execute after license is saved (for both create and update flows)
   */
  private proceedAfterLicenseSave(model: License, wasNewLicense?: boolean): void {
    // Determine if this is a new license (was created, not updated)
    // Use passed parameter if available (for create flow), otherwise check licenseExists (for update flow)
    const isNewLicense = wasNewLicense !== undefined ? wasNewLicense : !this.licenseExists;

    // Get extracted data for post-save operations
    const extractedData = this.registrationService.getExtractedLicenseData();

    // Check if we should auto-update driver profile
    // This applies to both new and existing licenses if DOB/address are empty
    if (extractedData && (extractedData.date_of_birth || this.hasAllAddressFields(extractedData))) {
      this.checkAndAutoUpdateDriverProfile(extractedData, isNewLicense);
    } else if (extractedData && extractedData.full_address) {
      // For existing licenses, show residence history confirmation dialog
      this.showResidenceHistoryConfirmationDialog(extractedData.full_address, model.issueDate);
    } else {
      // CRITICAL FIX: Refresh driver data before navigation to ensure green checkmark appears
      this.refreshAndNavigateToProfile();
    }
  }
  /**
   * Upload pending images after license is created or updated
   * NOTE: Since Step 2 now includes image extensions, the response already has fresh signed URLs
   * We can upload directly without making another API call
   * @param response - License response with signed URLs from Step 2
   * @param waitForCompletion - If true, waits for upload to complete before executing callback
   * @param onComplete - Optional callback to execute after images are uploaded
   */
  private uploadImagesAfterSave(response: any, waitForCompletion: boolean = false, onComplete?: () => void): void {
    // Upload images directly using the signed URLs from Step 2
    // No need for another API call since Step 2 already included extensions
    if (this.frontImageFile && this.backImageFile) {
      this.licenseDataService
        .sendImageFiles(this.frontImageFile, this.backImageFile, response)
        .pipe(
          first(),
          tap(uploadResponse => {
            if (uploadResponse) {
              this.fetchLicenseImageUrl('front');
              this.fetchLicenseImageUrl('back');

              if (this.frontImageFile) {
                const frontExt = this.frontImageFile.name.split('.').pop()?.toLowerCase() || 'png';
                this.registrationService.notifyCarriersOnDocumentUpload('DriverLicense', frontExt, this.frontImageFile, 'Driver License (Front)');
              }
              if (this.backImageFile) {
                const backExt = this.backImageFile.name.split('.').pop()?.toLowerCase() || 'png';
                this.registrationService.notifyCarriersOnDocumentUpload('DriverLicense', backExt, this.backImageFile, 'Driver License (Back)');
              }

              if (waitForCompletion && onComplete) {
                onComplete();
              }
            } else {
              console.error('[LICENSE SAVE] Image upload failed - no uploadResponse');
              this.toastService.showError('Failed to upload images. Please try again.');
              if (waitForCompletion && onComplete) {
                onComplete();
              }
            }
          }),
          catchError(error => {
            console.error('[LICENSE SAVE] Image upload ERROR:', error);
            this.toastService.showError('Failed to upload images. Please try again.');
            if (waitForCompletion && onComplete) {
              onComplete();
            }
            return of(false);
          })
        )
        .subscribe();
    } else {
      if (this.frontImageFile) {
        this.performImageUpload(response, this.frontImageFile, 'front');
      }
      if (this.backImageFile) {
        this.performImageUpload(response, this.backImageFile, 'back');
      }
      if (waitForCompletion && onComplete) {
        onComplete();
      }
    }
  }

  private getLicense(fetchLicense, goBack): void {
    if (fetchLicense) {
      this.loading = true;

      this.registrationService
        .getLicense()
        .pipe(
          tap((response) => {
            if (!goBack && response) {
              const license = response;
              this.licenseExists = true;
              this.driverLicenseId = license.id;

              // Check if front and back images exist - prioritize local files over backend URLs
              // Defer image URL updates to avoid ExpressionChangedAfterItHasBeenCheckedError
              setTimeout(() => {
                // If we have a local file (just uploaded), keep it and mark as existing
                if (this.frontImageFile) {
                  this.frontImageExists = true;
                } else if (license.frontScanImageUrl) {
                  this.frontImageUrl = this.domSanitizer.bypassSecurityTrustUrl(license.frontScanImageUrl);
                  this.frontImageExists = true;
                } else {
                  this.frontImageExists = false;
                }

                if (this.backImageFile) {
                  this.backImageExists = true;
                } else if (license.backScanImageUrl) {
                  this.backImageUrl = this.domSanitizer.bypassSecurityTrustUrl(license.backScanImageUrl);
                  this.backImageExists = true;
                } else {
                  this.backImageExists = false;
                }

                this.cdr.detectChanges();
              });

              // Fetch actual image URLs if images exist
              if (license.frontScanImagePresent) {
                this.fetchLicenseImageUrl('front');
              }
              if (license.backScanImagePresent) {
                this.fetchLicenseImageUrl('back');
              }

              if (license?.status) {
                this.currentStatus = license.status;
                if (this.currentStatus === BERBIX_STATUS_PROCESSING) {
                  this.currentStatusMessage = PROCESSSING_STATUS_MESSAGE;
                }
                if (this.currentStatus === BERBIX_STATUS_FAILED) {
                  this.currentStatusMessage = FAILED_STATUS_MESSAGE;
                }
              }

              this.setStateProvinces(license.issuingCountry);
              this.setLicense(license.issuingProvince);
              this.setMinDateFormExpiry(license.issueDate);

              this.populateForm(license);
              this.updateControlValidity(license.hasDeniedLicense, 'deniedLicenseExplanation');
              this.updateControlValidity(license.hasSuspendedLicense, 'suspendedLicenseExplanation');

              // Set isAirBrakeRequired flag based on province
              if (license.issuingProvince === 'ON') {
                this.isAirBrakeRequired = true;
              }

              // Check for extracted license data and auto-populate if form is mostly empty
              if (this.extractedLicenseData && this.shouldAutoPopulateFromExtractedData(license)) {
                this.autoPopulateFromExtractedData();
              }

              this.cdr.detectChanges();
            }
          }),
          catchError((error: any) => {
            if (goBack) {
              this.navigateToProfile();
            }
            return of(false);
          }),
          finalize(() => {
            this.loading = false;
            this.licenseLoaded = true;
          })
        )
        .subscribe((response: License) => {
          if (response && goBack) {
            // Check if license milestone is complete (both form data and images uploaded)
            const isLicenseComplete = response &&
              response.frontScanImageUrl &&
              response.backScanImageUrl &&
              response.licenseNumber &&
              response.expiryDate;

            if (isLicenseComplete) {
              // License milestone is complete, navigate to profile
              this.navigateToProfile();
            } else {
              // License milestone not complete, just go back
              this.goBack();
            }
          }
        });

    } else {
      this.loading = true;
      const license = this.registrationService.getLicenseStore();

      if (license) {
        this.licenseExists = true;
        this.driverLicenseId = license.id;

        // Check if front and back images exist - prioritize local files over backend URLs
        // Defer image URL updates to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          if (this.frontImageFile) {
            this.frontImageExists = true;
          } else if (license.frontScanImageUrl) {
            this.frontImageUrl = this.domSanitizer.bypassSecurityTrustUrl(license.frontScanImageUrl);
            this.frontImageExists = true;
          } else {
            this.frontImageExists = false;
          }

          if (this.backImageFile) {
            this.backImageExists = true;
          } else if (license.backScanImageUrl) {
            this.backImageUrl = this.domSanitizer.bypassSecurityTrustUrl(license.backScanImageUrl);
            this.backImageExists = true;
          } else {
            this.backImageExists = false;
          }

          this.cdr.detectChanges();
        });

        // Fetch actual image URLs if images exist
        if (license.frontScanImagePresent) {
          this.fetchLicenseImageUrl('front');
        }
        if (license.backScanImagePresent) {
          this.fetchLicenseImageUrl('back');
        }

        if (license?.status) {
          this.currentStatus = license.status;
          if (this.currentStatus === BERBIX_STATUS_PROCESSING) {
            this.currentStatusMessage = PROCESSSING_STATUS_MESSAGE;
          }
          if (this.currentStatus === BERBIX_STATUS_FAILED) {
            this.currentStatusMessage = FAILED_STATUS_MESSAGE;
          }
        }

        this.setStateProvinces(license.issuingCountry);
        this.setLicense(license.issuingProvince);
        this.setMinDateFormExpiry(license.issueDate);

        this.populateForm(license);
        this.updateControlValidity(license.hasDeniedLicense, 'deniedLicenseExplanation');
        this.updateControlValidity(license.hasSuspendedLicense, 'suspendedLicenseExplanation');

        // Set isAirBrakeRequired flag based on province
        if (license.issuingProvince === 'ON') {
          this.isAirBrakeRequired = true;
        }

        // Check for extracted license data and auto-populate if form is mostly empty
        if (this.extractedLicenseData && this.shouldAutoPopulateFromExtractedData(license)) {
          this.autoPopulateFromExtractedData();
        }

        this.cdr.detectChanges();
      }

      this.loading = false;
      this.licenseLoaded = true;
    }
  }

  populateForm(license) {
    this.isInitializing = true;

    this.licenseForm.setValue({
      licenseNumber: license.licenseNumber ?? '',
      issueDate: this.parseDate(license.issueDate) ?? '',
      expiryDate: this.parseDate(license.expiryDate) ?? '',
      issuingCountry: license.issuingCountry ?? '',
      issuingProvince: license.issuingProvince ?? '',
      licenseType: license.licenseType ?? '',
      hasAirBreakEndorsment: license.hasAirBreakEndorsment ?? null,
      hasDeniedLicense: license.hasDeniedLicense ?? null,
      deniedLicenseExplanation: license.deniedLicenseExplanation ?? '',
      hasSuspendedLicense: license.hasSuspendedLicense ?? null,
      suspendedLicenseExplanation: license.suspendedLicenseExplanation ?? null
    });

    // Store the initial form value for comparison later
    this.initialFormValue = this.licenseForm.value;
    // Reset the modification flag since we just loaded the form
    this.userHasModifiedForm = false;

    // Mark initialization as complete after a small delay to ensure all subscriptions have processed
    setTimeout(() => {
      this.isInitializing = false;
    }, 100);
  }

  /**
   * Open bottom sheet for uploading license images (combined front + back)
   */
  openUploadBottomSheet(licenseType?: 'front' | 'back'): void {
    const bottomSheetRef = this.bottomSheet.open(LicenseUploadBottomSheetComponent, {
      panelClass: 'license-upload-sheet',
      disableClose: true,
      hasBackdrop: true,
      data: {} // No longer need to pass license type
    });

    bottomSheetRef.afterDismissed().subscribe((result: LicenseUploadResult) => {
      if (result && result.frontImageFile && result.backImageFile) {
        // Store both images
        this.frontImageFile = result.frontImageFile;
        this.frontImageUrl = this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(result.frontImageFile));
        this.frontImageExists = true;

        this.backImageFile = result.backImageFile;
        this.backImageUrl = this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(result.backImageFile));
        this.backImageExists = true;

        // Auto-populate from extracted data
        if (result.extractedData) {
          this.extractedLicenseData = result.extractedData;
          this.autoPopulateFromExtractedData();
        }

        // Mark form as modified since images are uploaded
        this.userHasModifiedForm = true;

        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Handle image view click - Open lightbox dialog
   */
  onViewLicenseImage(licenseType: 'front' | 'back'): void {
    const imageUrl = licenseType === 'front' ? this.frontImageUrl : this.backImageUrl;

    if (!imageUrl) {
      return;
    }

    const dialogData: ImageLightboxData = {
      imageUrl: imageUrl,
      title: licenseType === 'front' ? 'Front License' : 'Back License',
      downloadFileName: `license-${licenseType}-${Date.now()}.jpg`
    };

    this.dialog.open(ImageLightboxDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      width: '800px',
      panelClass: 'image-lightbox-dialog',
      data: dialogData
    });
  }

  /**
   * Perform the actual file upload to S3
   */
  private performImageUpload(response: any, licenseImage: File, licenseType: 'front' | 'back'): void {
    const isFront = licenseType === 'front';
    const licenseUrl = isFront ? response.frontScanImageUrl : response.backScanImageUrl;
    const licenseNewName = isFront ? response.frontScanImageFile : response.backScanImageFile;

    if (!licenseUrl || !licenseNewName) {
      this.toastService.showError('Failed to get upload URL. Please try again.');
      this.isUploadingImage = false;
      return;
    }

    const fileExtension = licenseNewName.split('.').pop()?.toLowerCase() || '';
    let contentType: string;

    try {
      contentType = (mime as any).getType(fileExtension) || 'image/png';
    } catch (error) {
      // Fallback to manual content type mapping
      switch (fileExtension) {
        case 'jpg':
        case 'jpeg':
          contentType = 'image/jpeg';
          break;
        case 'png':
          contentType = 'image/png';
          break;
        case 'gif':
          contentType = 'image/gif';
          break;
        case 'webp':
          contentType = 'image/webp';
          break;
        default:
          contentType = 'image/png';
      }
    }

    const newFile = new File([licenseImage], licenseNewName, { type: contentType });
    const fileContentType = newFile.type ? newFile.type : 'image/png';

    this.fileDataService
      .uploadFile(newFile, licenseUrl, fileContentType)
      .pipe(
        first(),
        tap(uploadResponse => {
          if (uploadResponse) {
            // Image uploaded successfully - silently fetch the URL
            // Don't show success toast to avoid confusion (only show toast when license is SAVED)
            this.fetchLicenseImageUrl(licenseType);
          } else {
            this.toastService.showError('Failed to upload image. Please try again.');
          }
        }),
        catchError(error => {
          this.toastService.showError('Failed to upload image. Please try again.');
          return of(false);
        }),
        finalize(() => {
          this.isUploadingImage = false;
        })
      )
      .subscribe();
  }

  /**
   * Fetch license image URL from backend after successful upload
   */
  private fetchLicenseImageUrl(licenseType: 'front' | 'back'): void {
    if (!this.driverLicenseId) {
      return;
    }

    const imageType = licenseType === 'front' ? 'frontlicenseimage' : 'backlicenseimage';
    const imageUrlField = licenseType === 'front' ? 'frontScanFileUrl' : 'backScanFileUrl';

    this.registrationService
      .getLicense(false, undefined, imageType)
      .pipe(
        first(),
        tap((response: any) => {
          if (response && response[imageUrlField]) {
            const url = response[imageUrlField];

            // Clear local file since we now have the backend URL
            if (licenseType === 'front') {
              this.frontImageFile = null;
              this.frontImageUrl = this.domSanitizer.bypassSecurityTrustUrl(url);
              this.frontImageExists = true;
            } else {
              this.backImageFile = null;
              this.backImageUrl = this.domSanitizer.bypassSecurityTrustUrl(url);
              this.backImageExists = true;
            }

            this.cdr.detectChanges();
          }
        }),
        catchError(error => {
          // Silent fail - image might not be ready immediately after upload
          // User already has the preview from the local file, so this is not critical
          // Don't show error toast to avoid confusing the user
          return of(null);
        })
      )
      .subscribe();
  }

  private parseDate(dateString: string): Date {
    const [year, month, day] = dateString
      .split('T')[0]
      .split('-')
      .map(Number);
    return new Date(year, month - 1, day);
  }

  private setLicense(_province: string): void {
    this.provinceLicenseClass = LICENSE_CLASSES_ALL;
  }
  setMinDateFormExpiry(issueDate: string | Date): void {
    this.expiryMinDate.setDate(this.expiryMinDate.getDate());
  }

  /**
   * Check if we should auto-populate from extracted data
   * Only auto-populate if key fields are empty
   */
  private shouldAutoPopulateFromExtractedData(license: License): boolean {
    return !license.licenseNumber || !license.issueDate || !license.expiryDate;
  }

  /**
   * TEMPORARY FIX: Correct province/state matching issues from Document Extract API
   * 
   * The API sometimes returns incorrect province codes when multiple options have
   * equal confidence scores (e.g., "Ontario" matches both "NT" and "ON" with score 0.85,
   * but returns "NT" because it comes first alphabetically).
   * 
   * This method checks the raw extracted value and corrects known mismatches.
   * 
   * TODO: Remove this once backend implements proper tie-breaking logic
   * See: [Link to backend ticket when created]
   */
  private fixProvinceMatching(extractedData: any): void {
    if (!extractedData?._metadata?.dropdown_matches?.issuing_province) {
      return;
    }

    const provinceMatch = extractedData._metadata.dropdown_matches.issuing_province;
    const rawValue = (provinceMatch.extracted_raw || '').toLowerCase().trim();
    const currentValue = extractedData.issuing_province;

    // Map of full province/state names to their correct codes
    const provinceCorrections: Record<string, string> = {
      // Canadian provinces
      'ontario': 'ON',
      'quebec': 'QC',
      'québec': 'QC',
      'british columbia': 'BC',
      'alberta': 'AB',
      'manitoba': 'MB',
      'saskatchewan': 'SK',
      'nova scotia': 'NS',
      'new brunswick': 'NB',
      'newfoundland': 'NL',
      'newfoundland and labrador': 'NL',
      'prince edward island': 'PE',
      'northwest territories': 'NT',
      'nunavut': 'NU',
      'yukon': 'YT',

      // US states (common ones)
      'california': 'US-CA',
      'new york': 'US-NY',
      'texas': 'US-TX',
      'florida': 'US-FL',
      'illinois': 'US-IL',
      'pennsylvania': 'US-PA',
      'ohio': 'US-OH',
      'georgia': 'US-GA',
      'michigan': 'US-MI',
      'north carolina': 'US-NC',
      'new jersey': 'US-NJ',
      'virginia': 'US-VA',
      'washington': 'US-WA',
      'arizona': 'US-AZ',
      'massachusetts': 'US-MA',
      'tennessee': 'US-TN',
      'indiana': 'US-IN',
      'missouri': 'US-MO',
      'maryland': 'US-MD',
      'wisconsin': 'US-WI',
      'colorado': 'US-CO',
      'minnesota': 'US-MN',
      'south carolina': 'US-SC',
      'alabama': 'US-AL',
      'louisiana': 'US-LA',
      'kentucky': 'US-KY',
      'oregon': 'US-OR',
      'oklahoma': 'US-OK',
      'connecticut': 'US-CT',
      'utah': 'US-UT',
      'iowa': 'US-IA',
      'nevada': 'US-NV',
      'arkansas': 'US-AR',
      'mississippi': 'US-MS',
      'kansas': 'US-KS',
      'new mexico': 'US-NM',
      'nebraska': 'US-NE',
      'west virginia': 'US-WV',
      'idaho': 'US-ID',
      'hawaii': 'US-HI',
      'new hampshire': 'US-NH',
      'maine': 'US-ME',
      'rhode island': 'US-RI',
      'montana': 'US-MT',
      'delaware': 'US-DE',
      'south dakota': 'US-SD',
      'north dakota': 'US-ND',
      'alaska': 'US-AK',
      'vermont': 'US-VT',
      'wyoming': 'US-WY'
    };

    // Check if raw value contains any known province/state name
    for (const [fullName, correctCode] of Object.entries(provinceCorrections)) {
      if (rawValue.includes(fullName) && currentValue !== correctCode) {
        extractedData.issuing_province = correctCode;
        break;
      }
    }
  }

  /**
   * Auto-populate form fields from Document Extract API data
   */
  private autoPopulateFromExtractedData(): void {
    if (!this.extractedLicenseData) {
      return;
    }

    // TEMPORARY FIX: Correct province matching issues from Document Extract API
    // TODO: Remove this once backend implements proper tie-breaking logic
    this.fixProvinceMatching(this.extractedLicenseData);

    this.isInitializing = true;

    // Map extracted data to form fields
    const mappedData = this.mapExtractedDataToFormFields(this.extractedLicenseData);

    // Patch the form with extracted data (replace existing values with new extraction)
    Object.keys(mappedData).forEach(key => {
      const value = mappedData[key];
      if (value !== null && value !== undefined && value !== '') {
        // Always update with extracted data (overwrite existing values)
        this.licenseForm.get(key)?.patchValue(value);
      }
    });

    // FOR NEW LICENSES: Default declaration fields to false if they're still null
    // This ensures licenseRequirementsCompleted can be true after first save
    if (!this.licenseExists) {
      if (this.licenseForm.get('hasDeniedLicense')?.value === null) {
        this.licenseForm.get('hasDeniedLicense')?.patchValue(false);
      }
      if (this.licenseForm.get('hasSuspendedLicense')?.value === null) {
        this.licenseForm.get('hasSuspendedLicense')?.patchValue(false);
      }
      // hasAirBreakEndorsment only required for ON, so default to false if province is ON
      if (this.isAirBrakeRequired && this.licenseForm.get('hasAirBreakEndorsment')?.value === null) {
        this.licenseForm.get('hasAirBreakEndorsment')?.patchValue(false);
      }
    }

    // Mark all fields as touched and dirty to trigger validation messages
    Object.keys(this.licenseForm.controls).forEach(key => {
      const control = this.licenseForm.get(key);
      control?.markAsTouched();
      control?.markAsDirty();
    });

    // Update dropdowns based on extracted country/province
    if (mappedData.issuingCountry) {
      this.setStateProvinces(mappedData.issuingCountry);
    }
    if (mappedData.issuingProvince) {
      this.setLicense(mappedData.issuingProvince);
    }
    if (mappedData.issueDate) {
      this.setMinDateFormExpiry(mappedData.issueDate);
    }

    // Validate dropdown values after dropdowns are populated
    // Use setTimeout to ensure dropdowns have updated
    setTimeout(() => {
      // Validate country
      if (mappedData.issuingCountry && !this.isValidCountry(mappedData.issuingCountry)) {
        this.licenseForm.get('issuingCountry')?.patchValue('');
      }

      // Validate province/state
      if (mappedData.issuingProvince && !this.isValidProvince(mappedData.issuingProvince)) {
        this.licenseForm.get('issuingProvince')?.patchValue('');
      }

      // Validate license type
      if (mappedData.licenseType && !this.isValidLicenseType(mappedData.licenseType)) {
        this.licenseForm.get('licenseType')?.patchValue('');
      }
    }, 50);

    // Store the full mapped data (including address, DOB, name) back to registration service
    // for use in onSubmit (residence history dialog, DOB verification)
    this.registrationService.storeExtractedLicenseData(mappedData);
    this.extractedLicenseData = null;

    // End initialization phase and update validators to show validation messages
    setTimeout(() => {
      this.isInitializing = false;

      // Force validation update for all controls to show errors immediately
      Object.keys(this.licenseForm.controls).forEach(key => {
        const control = this.licenseForm.get(key);
        control?.updateValueAndValidity({ emitEvent: true });
      });

      this.cdr.detectChanges();
    }, 100);
  }

  /**
   * Map extracted data fields to form control names with proper transformations
   */
  private mapExtractedDataToFormFields(extractedData: any): any {
    const mapped: any = {};

    // License Number - direct mapping
    if (extractedData.license_number) {
      mapped.licenseNumber = extractedData.license_number;
    }

    // Issue Date - parse string to Date
    if (extractedData.issue_date) {
      mapped.issueDate = this.parseDateFromExtracted(extractedData.issue_date);
    }

    // Expiry Date - parse string to Date
    if (extractedData.expiry_date) {
      mapped.expiryDate = this.parseDateFromExtracted(extractedData.expiry_date);
    }

    // Country - map to dropdown value
    if (extractedData.issuing_country) {
      const country = extractedData.issuing_country.toLowerCase();
      if (country === 'canada' || country === 'ca') {
        mapped.issuingCountry = 'ca';
      } else if (country === 'usa' || country === 'us' || country === 'united states') {
        mapped.issuingCountry = 'us';
      }
    }

    // Province/State - map to dropdown value
    if (extractedData.issuing_province) {
      mapped.issuingProvince = this.mapProvinceCode(extractedData.issuing_province);
    }

    // License Type/Class - map to dropdown value
    if (extractedData.license_type) {
      mapped.licenseType = this.mapLicenseType(extractedData.license_type);
    }

    // Address fields - preserve ALL for driver profile auto-update and residence history
    if (extractedData.full_address) {
      mapped.full_address = extractedData.full_address;
    }
    if (extractedData.address_street) {
      mapped.address_street = extractedData.address_street;
    }
    if (extractedData.address_city) {
      mapped.address_city = extractedData.address_city;
    }
    if (extractedData.address_subdivision) {
      mapped.address_subdivision = extractedData.address_subdivision;
    }
    if (extractedData.address_postal_code) {
      mapped.address_postal_code = extractedData.address_postal_code;
    }
    if (extractedData.address_country) {
      mapped.address_country = extractedData.address_country;
    }

    // Date of Birth - store for verification and driver profile auto-update
    if (extractedData.date_of_birth) {
      mapped.date_of_birth = extractedData.date_of_birth;
    }

    // Gender - store for driver profile auto-update
    if (extractedData.gender) {
      mapped.gender = extractedData.gender.toLowerCase(); // Ensure lowercase
    }

    // Full Name - store for verification
    if (extractedData.full_name) {
      mapped.full_name = extractedData.full_name;
    }

    return mapped;
  }

  /**
   * Parse date from extracted data (handles various formats)
   */
  private parseDateFromExtracted(dateString: string): Date | null {
    if (!dateString) {
      return null;
    }

    try {
      // Try parsing with dayjs (handles multiple formats)
      const parsed = dayjs(dateString);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    } catch (error) {
      // Silent fail - return null for invalid dates
    }

    return null;
  }

  /**
   * Map province/state code from extracted data to form dropdown value
   */
  private mapProvinceCode(province: string): string {
    if (!province) {
      return '';
    }

    // Normalize province string
    const normalized = province.toUpperCase().trim();

    // Canadian provinces
    const canadianProvinces: { [key: string]: string } = {
      'ONTARIO': 'ON',
      'ON': 'ON',
      'QUEBEC': 'QC',
      'QC': 'QC',
      'BRITISH COLUMBIA': 'BC',
      'BC': 'BC',
      'ALBERTA': 'AB',
      'AB': 'AB',
      'MANITOBA': 'MB',
      'MB': 'MB',
      'SASKATCHEWAN': 'SK',
      'SK': 'SK',
      'NOVA SCOTIA': 'NS',
      'NS': 'NS',
      'NEW BRUNSWICK': 'NB',
      'NB': 'NB',
      'NEWFOUNDLAND AND LABRADOR': 'NL',
      'NL': 'NL',
      'PRINCE EDWARD ISLAND': 'PE',
      'PE': 'PE',
      'NORTHWEST TERRITORIES': 'NT',
      'NT': 'NT',
      'YUKON': 'YT',
      'YT': 'YT',
      'NUNAVUT': 'NU',
      'NU': 'NU'
    };

    // US states - map to US-XX format
    const usStates: { [key: string]: string } = {
      'ALABAMA': 'US-AL',
      'AL': 'US-AL',
      'ALASKA': 'US-AK',
      'AK': 'US-AK',
      'ARIZONA': 'US-AZ',
      'AZ': 'US-AZ',
      'ARKANSAS': 'US-AR',
      'AR': 'US-AR',
      'CALIFORNIA': 'US-CA',
      'CA': 'US-CA',
      'COLORADO': 'US-CO',
      'CO': 'US-CO',
      'CONNECTICUT': 'US-CT',
      'CT': 'US-CT',
      'DELAWARE': 'US-DE',
      'DE': 'US-DE',
      'FLORIDA': 'US-FL',
      'FL': 'US-FL',
      'GEORGIA': 'US-GA',
      'GA': 'US-GA',
      'HAWAII': 'US-HI',
      'HI': 'US-HI',
      'IDAHO': 'US-ID',
      'ID': 'US-ID',
      'ILLINOIS': 'US-IL',
      'IL': 'US-IL',
      'INDIANA': 'US-IN',
      'IN': 'US-IN',
      'IOWA': 'US-IA',
      'IA': 'US-IA',
      'KANSAS': 'US-KS',
      'KS': 'US-KS',
      'KENTUCKY': 'US-KY',
      'KY': 'US-KY',
      'LOUISIANA': 'US-LA',
      'LA': 'US-LA',
      'MAINE': 'US-ME',
      'ME': 'US-ME',
      'MARYLAND': 'US-MD',
      'MD': 'US-MD',
      'MASSACHUSETTS': 'US-MA',
      'MA': 'US-MA',
      'MICHIGAN': 'US-MI',
      'MI': 'US-MI',
      'MINNESOTA': 'US-MN',
      'MN': 'US-MN',
      'MISSISSIPPI': 'US-MS',
      'MS': 'US-MS',
      'MISSOURI': 'US-MO',
      'MO': 'US-MO',
      'MONTANA': 'US-MT',
      'MT': 'US-MT',
      'NEBRASKA': 'US-NE',
      'NE': 'US-NE',
      'NEVADA': 'US-NV',
      'NV': 'US-NV',
      'NEW HAMPSHIRE': 'US-NH',
      'NH': 'US-NH',
      'NEW JERSEY': 'US-NJ',
      'NJ': 'US-NJ',
      'NEW MEXICO': 'US-NM',
      'NM': 'US-NM',
      'NEW YORK': 'US-NY',
      'NY': 'US-NY',
      'NORTH CAROLINA': 'US-NC',
      'NC': 'US-NC',
      'NORTH DAKOTA': 'US-ND',
      'ND': 'US-ND',
      'OHIO': 'US-OH',
      'OH': 'US-OH',
      'OKLAHOMA': 'US-OK',
      'OK': 'US-OK',
      'OREGON': 'US-OR',
      'OR': 'US-OR',
      'PENNSYLVANIA': 'US-PA',
      'PA': 'US-PA',
      'RHODE ISLAND': 'US-RI',
      'RI': 'US-RI',
      'SOUTH CAROLINA': 'US-SC',
      'SC': 'US-SC',
      'SOUTH DAKOTA': 'US-SD',
      'SD': 'US-SD',
      'TENNESSEE': 'US-TN',
      'TN': 'US-TN',
      'TEXAS': 'US-TX',
      'TX': 'US-TX',
      'UTAH': 'US-UT',
      'UT': 'US-UT',
      'VERMONT': 'US-VT',
      'VT': 'US-VT',
      'VIRGINIA': 'US-VA',
      'VA': 'US-VA',
      'WASHINGTON': 'US-WA',
      'WA': 'US-WA',
      'WEST VIRGINIA': 'US-WV',
      'WV': 'US-WV',
      'WISCONSIN': 'US-WI',
      'WI': 'US-WI',
      'WYOMING': 'US-WY',
      'WY': 'US-WY'
    };

    // Try Canadian provinces first, then US states
    const result = canadianProvinces[normalized] || usStates[normalized] || normalized;
    return result;
  }

  /**
   * Map license type/class from extracted data to form dropdown value
   */
  private mapLicenseType(licenseType: string): string {
    if (!licenseType) {
      return '';
    }

    // Normalize license type
    const normalized = licenseType.toUpperCase().trim();

    // Common mappings
    const typeMappings: { [key: string]: string } = {
      'CLASS A': 'Class A',
      'CLASS B': 'Class B',
      'CLASS C': 'Class C',
      'CLASS D': 'Class D',
      'CLASS E': 'Class E',
      'CLASS F': 'Class F',
      'CLASS G': 'Class G',
      'CLASS 1': 'Class 1',
      'CLASS 2': 'Class 2',
      'CLASS 3': 'Class 3',
      'CLASS 4': 'Class 4',
      'CLASS 5': 'Class 5',
      'A': 'Class A',
      'B': 'Class B',
      'C': 'Class C',
      'D': 'Class D',
      'E': 'Class E',
      'F': 'Class F',
      'G': 'Class G',
      '1': 'Class 1',
      '2': 'Class 2',
      '3': 'Class 3',
      '4': 'Class 4',
      '5': 'Class 5'
    };

    // Check if it's a direct match
    if (typeMappings[normalized]) {
      return typeMappings[normalized];
    }

    // Handle combined license classes (e.g., "AC" = Class A + C, "1F" = Class 1 + F)
    // For combined classes, try to extract the first/primary class
    if (normalized.length >= 1) {
      const firstChar = normalized.charAt(0);
      if (typeMappings[firstChar]) {
        return typeMappings[firstChar];
      }
    }

    // If no match found, return empty string to trigger validation error
    return '';
  }

  /**
   * Check if a province/state value exists in the dropdown options
   */
  private isValidProvince(province: string): boolean {
    if (!province || !this.stateProvinces || this.stateProvinces.length === 0) {
      return false;
    }
    return this.stateProvinces.some(p => p.value === province);
  }

  /**
   * Check if a license type exists in the dropdown options
   */
  private isValidLicenseType(licenseType: string): boolean {
    if (!licenseType || !this.provinceLicenseClass || this.provinceLicenseClass.length === 0) {
      return false;
    }
    return this.provinceLicenseClass.includes(licenseType);
  }

  /**
   * Check if a country value exists in the dropdown options
   */
  private isValidCountry(country: string): boolean {
    if (!country || !this.issuingCountry || this.issuingCountry.length === 0) {
      return false;
    }
    return this.issuingCountry.some(c => c.code === country);
  }

  /**
   * Show confirmation dialog for updating residence history
   */
  private showResidenceHistoryConfirmationDialog(extractedAddress: string, issueDate: string | Date): void {
    // Get extracted data to check for DOB
    const extractedData = this.registrationService.getExtractedLicenseData();
    const extractedDOB = extractedData?.date_of_birth;

    // Get both current residence and driver profile in parallel
    this.driverApiService.getAddresses().pipe(
      first(),
      catchError(() => of({ data: [] }))
    ).subscribe((response) => {
      // Unwrap the response data
      const addressArray = Array.isArray(response?.data) ? response.data : [];
      const currentResidence = addressArray.find(addr => addr && addr.isCurrentAddress);
      const currentAddressText = currentResidence
        ? `${currentResidence.streetAddress}, ${currentResidence.city}, ${currentResidence.province}, ${currentResidence.postalCode}`
        : 'No current residence on file';

      const formattedAddress = this.formatAddressForDisplay(extractedAddress);

      // Get driver profile for DOB comparison
      this.driverApiService.getDriver().pipe(
        first(),
        catchError(() => of(null))
      ).subscribe((driver) => {
        const profileDOB = driver?.dateOfBirth;

        // Format dates for comparison and display
        let extractedDOBFormatted = null;
        let profileDOBFormatted = null;

        if (extractedDOB) {
          const extractedDate = dayjs(extractedDOB);
          if (extractedDate.isValid()) {
            extractedDOBFormatted = extractedDate.format('MMM DD, YYYY');
          }
        }

        if (profileDOB) {
          const profileDate = dayjs(profileDOB);
          if (profileDate.isValid()) {
            profileDOBFormatted = profileDate.format('MMM DD, YYYY');
          }
        }

        const dialogRef = this.dialog.open(ResidenceHistoryDialogComponent, {
          width: '550px',
          maxWidth: '95vw',
          data: {
            currentAddress: currentAddressText,
            newAddress: formattedAddress,
            extractedDateOfBirth: extractedDOBFormatted,
            profileDateOfBirth: profileDOBFormatted
          },
          disableClose: true,
          hasBackdrop: true,
          backdropClass: 'residence-dialog-backdrop'
        });

        // Subscribe to the confirm button click
        const dialogInstance = dialogRef.componentInstance;
        const originalOnConfirm = dialogInstance.onConfirm.bind(dialogInstance);

        dialogInstance.onConfirm = () => {
          originalOnConfirm();
          this.createResidenceHistoryFromLicense(extractedAddress, issueDate, dialogRef);
        };

        dialogRef.afterClosed().subscribe((result) => {
          // If user cancelled (clicked "No, Keep Existing"), clear and navigate
          if (result === false) {
            this.registrationService.clearExtractedLicenseData();
            this.navigateToProfile();
          }
        });
      });
    });
  }

  /**
   * Create residence history entry from license address
   */
  private createResidenceHistoryFromLicense(
    extractedAddress: string,
    issueDate: string | Date,
    dialogRef: MatDialogRef<ResidenceHistoryDialogComponent>
  ): void {
    // Get extracted data which has individual address fields
    const extractedData = this.registrationService.getExtractedLicenseData();

    // Use extracted individual address fields if available, otherwise fall back to parsing
    let parsedAddress: any;

    if (extractedData && extractedData.address_street && extractedData.address_city &&
      extractedData.address_subdivision && extractedData.address_postal_code) {
      // Use individual address fields from extraction
      parsedAddress = {
        streetAddress: extractedData.address_street,
        city: extractedData.address_city,
        state: extractedData.address_subdivision,
        zipcode: extractedData.address_postal_code,
        country: extractedData.address_country || 'CA'
      };
    } else {
      // Fall back to parsing the full address string
      parsedAddress = this.parseAddressForResidenceHistory(extractedAddress);
    }

    if (!parsedAddress || !parsedAddress.streetAddress) {
      this.toastService.showError('Unable to parse address. Please add residence history manually.');
      dialogRef.close();
      this.registrationService.clearExtractedLicenseData();
      this.navigateToProfile();
      return;
    }

    // Format the date - use first day of current month (consistent with residence history form)
    const today = dayjs();
    const firstDayOfCurrentMonth = today.startOf('month').format('YYYY-MM-DD');

    // Normalize country code to lowercase (API expects 'ca' or 'us', not 'CA' or 'US')
    let countryCode = (parsedAddress.country || 'CA').toLowerCase();
    if (countryCode === 'canada') {
      countryCode = 'ca';
    } else if (countryCode === 'usa' || countryCode === 'united states') {
      countryCode = 'us';
    }

    // Create residence history model for the new current address
    // Use first day of current month as start date (consistent with residence history form)
    const newCurrentAddressModel = {
      completeAddress: extractedAddress, // Full address string as it appears on license
      streetAddress: parsedAddress.streetAddress,
      city: parsedAddress.city,
      province: parsedAddress.state,
      postalCode: parsedAddress.zipcode,
      country: countryCode, // API expects lowercase: 'ca' or 'us'
      livedFromDate: firstDayOfCurrentMonth, // First day of current month
      isCurrentAddress: true
    };

    // Check if a current address already exists
    this.driverApiService.getAddresses().pipe(
      first(),
      catchError(() => of({ data: [] }))
    ).subscribe((response) => {
      // Unwrap the response data
      const addressArray = Array.isArray(response?.data) ? response.data : [];
      const existingCurrentAddress = addressArray.find(addr => addr && addr.isCurrentAddress);

      if (existingCurrentAddress && existingCurrentAddress.id) {
        // Option 2: Preserve history by converting current to previous, then creating new
        // Step 1: Calculate the "to date" for the previous address
        // Use month boundaries for address transitions (consistent with residence history form)
        const today = dayjs();
        const previousAddressToDate = today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'); // Last day of previous month

        // New address starts first day of current month
        const newAddressStartDate = today.startOf('month').format('YYYY-MM-DD'); // First day of current month

        // Check if existing address's livedFromDate would be after the new livedToDate
        // This can happen if the address was created with first day of current month
        const existingFromDate = dayjs(existingCurrentAddress.livedFromDate);
        const newToDate = dayjs(previousAddressToDate);
        let previousAddressFromDate: string | undefined = undefined;

        if (existingFromDate.isAfter(newToDate)) {
          // FROM date is after TO date - need to adjust FROM to first day of TO date's month
          previousAddressFromDate = newToDate.startOf('month').format('YYYY-MM-DD');
        }

        // Create model for PATCH: only include fields that need to change
        // PATCH should only update the status and add the end date, not replace the entire address
        const previousAddressModel: any = {
          livedToDate: previousAddressToDate, // Last day of previous month
          isCurrentAddress: false // ✅ Explicitly set to false to convert to previous address
        };

        // Include livedFromDate only if it needs to be adjusted
        if (previousAddressFromDate) {
          previousAddressModel.livedFromDate = previousAddressFromDate;
        }

        // Update the new address model to use today's date
        const updatedNewAddressModel = {
          ...newCurrentAddressModel,
          livedFromDate: newAddressStartDate // Start date = first day of current month
        };

        // Step 1: Update existing current address to previous address
        this.subs.sink = this.driverApiService
          .updateAddress(previousAddressModel, existingCurrentAddress.id)
          .pipe(
            first(),
            catchError((error) => {
              this.toastService.showError('Failed to update residence history. Please add it manually.');
              dialogRef.componentInstance.isProcessing = false;
              dialogRef.close();
              this.registrationService.clearExtractedLicenseData();
              this.navigateToProfile();
              return of(false);
            })
          )
          .subscribe((updateResponse) => {
            if (updateResponse) {
              // Step 2: Create new residence address only (no automatic profile update)
              this.subs.sink = this.driverApiService
                .addAddress(updatedNewAddressModel)
                .pipe(
                  first(),
                  catchError((error) => {
                    this.toastService.showError('Failed to add new address. Please try again.');
                    dialogRef.componentInstance.isProcessing = false;
                    dialogRef.close();
                    return of(false);
                  })
                )
                .subscribe((createResponse) => {
                  if (createResponse) {
                    dialogRef.componentInstance.isProcessing = false;
                    dialogRef.close();
                    // Show second confirmation dialog for contact info update
                    this.showContactInfoConfirmationDialog(extractedAddress, parsedAddress);
                  }
                });
            }
          });
      } else {
        // No current address exists, create new one directly
        this.subs.sink = this.driverApiService
          .addAddress(newCurrentAddressModel)
          .pipe(
            first(),
            catchError((error) => {
              this.toastService.showError('Failed to create residence history. Please try again.');
              dialogRef.componentInstance.isProcessing = false;
              dialogRef.close();
              return of(false);
            })
          )
          .subscribe((response) => {
            if (response) {
              dialogRef.componentInstance.isProcessing = false;
              dialogRef.close();
              // Show second confirmation dialog for contact info update
              this.showContactInfoConfirmationDialog(extractedAddress, parsedAddress);
            }
          });
      }
    });
  }

  /**
   * Parse full address string into components
   * Handles multiple formats:
   * - "Street City, Province, PostalCode" (3 parts) - from Document Extract API
   * - "Street, City, Province PostalCode, Country" (4 parts) - legacy format
   */
  parseAddressForResidenceHistory(address: string): any {
    if (!address) {
      return null;
    }

    const parsedAddressArr = address?.split(',');
    const addressModel: any = {};

    // Handle 3-part format: "Street City, Province, PostalCode"
    if (parsedAddressArr.length === 3) {
      const part1 = parsedAddressArr[0]?.trim(); // "71 TEMPLEHILL RD BRAMPTON"
      const part2 = parsedAddressArr[1]?.trim(); // "ON"
      const part3 = parsedAddressArr[2]?.trim(); // "L6R 3S4"

      // Split first part to get street and city
      const part1Words = part1.split(' ');
      const city = part1Words[part1Words.length - 1]; // Last word is city
      const streetAddress = part1Words.slice(0, -1).join(' '); // Everything before last word is street

      addressModel.fullAddress = address;
      addressModel.streetAddress = streetAddress;
      addressModel.city = city;
      addressModel.state = part2; // Province code (e.g., "ON")
      addressModel.zipcode = part3; // Postal code (e.g., "L6R 3S4")
      addressModel.country = 'CA'; // Default to Canada for 3-part format (will be lowercased later)
    }
    // Handle 4-part format: "Street, City, Province PostalCode, Country"
    else if (parsedAddressArr.length === 4) {
      addressModel.fullAddress = address;
      addressModel.streetAddress = parsedAddressArr[0]?.trim();
      addressModel.city = parsedAddressArr[1]?.trim();
      addressModel.state = parsedAddressArr[2]?.trim();
      addressModel.zipcode = addressModel.state.substring(3, addressModel.state.length)?.trim();
      addressModel.state = addressModel.state?.split(' ')[0];
      addressModel.country = parsedAddressArr[3]?.trim();

      // Normalize country codes (will be lowercased later in createResidenceHistoryFromLicense)
      if (addressModel.country === 'Canada' || addressModel.country === 'CA') {
        addressModel.country = 'CA';
      }
      if (addressModel.country === 'USA' || addressModel.country === 'US') {
        addressModel.country = 'US';
        addressModel.state = `US-${addressModel.state}`;
      }
    }

    return addressModel;
  }

  /**
   * Compare two addresses to determine if they are the same
   */
  compareAddresses(address1: string, address2: string): boolean {
    if (!address1 || !address2) {
      return false;
    }

    // Normalize addresses for comparison
    const normalize = (addr: string) => addr.toLowerCase().replace(/[^a-z0-9]/g, '');
    return normalize(address1) === normalize(address2);
  }

  /**
   * Format address for display in confirmation dialog
   */
  formatAddressForDisplay(address: string): string {
    if (!address) {
      return '';
    }
    return address.trim();
  }

  /**
   * Show confirmation dialog for updating contact information
   * Called after residence history is successfully updated
   * This dialog just asks if they want to go to profile to update their contact info
   */
  private showContactInfoConfirmationDialog(extractedAddress: string, parsedAddress: any): void {
    const formattedExtractedAddress = this.formatAddressForDisplay(extractedAddress);

    // Open simple confirmation dialog
    const dialogRef = this.dialog.open(ResidenceHistoryDialogComponent, {
      width: '500px',
      maxWidth: '95vw',
      data: {
        newAddress: formattedExtractedAddress,
        isContactInfoDialog: true // Flag to customize dialog text
      },
      disableClose: false,
      hasBackdrop: true,
      backdropClass: 'residence-dialog-backdrop'
    });

    dialogRef.afterClosed().subscribe((result) => {
      // Show success message
      this.toastService.showSuccess('Residence history updated successfully!');
      this.registrationService.clearExtractedLicenseData();

      if (result === true) {
        // User clicked "Go to Profile" - navigate to Edit Profile
        this.router.navigate(['/profile/personalInfo']);
      } else {
        // User clicked "Not Now" - navigate to home
        this.navigateToProfile();
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
